import { ActivateUserStatus } from 'tools/bff/generated/user/user_administration_pb';
import { Box, Modal, Text, Textarea } from '@tocktix/pantry';
import { Form, Formik, FormikErrors } from 'formik';
import { FunctionComponent, useState } from 'react';
import { useActions } from 'utils/connect';
import { userClient } from 'tools/bff/client';

interface ReactivateUserFormValues {
  reason: string;
}

interface ReactivateUserModalProps {
  uuid: string;
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}

const validate = (values: ReactivateUserFormValues) => {
  const errors: FormikErrors<ReactivateUserFormValues> = {};
  if (!values.reason.trim()) {
    errors.reason = 'Reason is required';
  }
  return errors;
};

const ReactivateUserModal: FunctionComponent<ReactivateUserModalProps> = ({
  uuid,
  open,
  onClose,
  onSubmit,
}) => {
  const [error, setError] = useState<string | null>(null);
  const { actions } = useActions();

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmission = async (values: ReactivateUserFormValues) => {
    setError(null);
    try {
      const response = await userClient.activateUsers({
        uuids: [uuid],
        reason: values.reason.trim(),
      });
      const result = response.results[0];
      switch (result?.status) {
        case ActivateUserStatus.REACTIVATED:
          await onSubmit();
          handleClose();
          actions.toast.showInfo('Account reactivated');
          return;
        case ActivateUserStatus.NOT_FOUND:
          setError('User not found.');
          actions.toast.showError('User not found.');
          return;
        default:
          setError('Error reactivating user' + (result?.error ? ': ' + result.error : '.'));
          actions.toast.showError('Error reactivating user');
          return;
      }
    } catch (reason) {
      setError('Error reactivating user: ' + reason);
      actions.toast.showError('Error reactivating user');
    }
  };

  return (
    <Formik<ReactivateUserFormValues>
      initialValues={{ reason: '' }}
      validate={validate}
      onSubmit={handleSubmission}
    >
      {({ dirty, errors, handleSubmit, isSubmitting, isValid, setFieldValue, touched, values }) => (
        <Form id="reactivate-user-form" data-testid="reactivate-user-form">
          <Modal
            id="reactivate-user-modal"
            open={open}
            onClose={handleClose}
            size="md"
            data-testid="reactivate-user-modal"
          >
            <Modal.Header title="Reactivate account" onClose={handleClose} border />
            <Modal.Body>
              <Box display="flex" flexDirection="column" gap={200}>
                <Text variant="body" color="fgSubtle">
                  This will reactivate the account and restore user access.
                </Text>
                <Textarea
                  id="reactivate-user-reason"
                  label="Reason"
                  value={values.reason}
                  onChange={(event) => setFieldValue('reason', event.target.value)}
                  required
                  fullWidth
                  data-testid="reactivate-user-reason-input"
                  validationStatus={touched.reason && errors.reason ? 'error' : undefined}
                  validationMessage={touched.reason ? errors.reason : undefined}
                />
                {error && (
                  <Text variant="body" color="fgDangerBase">
                    {error}
                  </Text>
                )}
              </Box>
            </Modal.Body>
            <Modal.Footer
              actions={{
                primary: {
                  label: isSubmitting ? 'Reactivating...' : 'Reactivate',
                  type: 'submit',
                  disabled: !dirty || !isValid || isSubmitting,
                  onClick: () => handleSubmit(),
                },
                cancel: {
                  label: 'Cancel',
                  onClick: handleClose,
                },
              }}
            />
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default ReactivateUserModal;
